import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEquipment } from '@services/productOfferingService';
import { selectParsedEquipment } from '@utils/equipmentMapper';
import {
  determineEquipmentPopupAction,
  getAutoAddEquipment,
  type EquipmentPopupAction,
} from '@utils/equipmentPopupLogic';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import type { ProductOfferingDTO } from '@t/api.types';

/**
 * Resolves the equipment popup action for a plan.
 *
 * - `resolve` fetches equipment and determines the action without side effects.
 * - `autoAddEquipment` silently adds the single free equipment to the cart
 *   (call only after the plan is already in the cart).
 */
export const useEquipmentAction = (sessionId: string, homeId: string) => {
  const queryClient = useQueryClient();
  const { selectAddOnAsync } = useShoppingCart(sessionId);

  /** Fetch equipment and determine popup action — no cart mutations. */
  const resolve = useCallback(
    async (planId: string): Promise<EquipmentPopupAction> => {
      const equipmentDTOs = await queryClient.fetchQuery<ProductOfferingDTO[]>({
        queryKey: ['equipment', sessionId, homeId, planId],
        queryFn: () => fetchEquipment(sessionId, homeId, planId),
        staleTime: Infinity,
      });

      const parsed = selectParsedEquipment(equipmentDTOs);
      return determineEquipmentPopupAction(parsed);
    },
    [sessionId, homeId, queryClient]
  );

  /** Add the single free equipment to the cart. Call only when action is 'auto-add' and the plan is already in the cart. */
  const autoAddEquipment = useCallback(
    async (planId: string) => {
      const equipmentDTOs = queryClient.getQueryData<ProductOfferingDTO[]>(['equipment', sessionId, homeId, planId]);
      if (!equipmentDTOs) return;

      const parsed = selectParsedEquipment(equipmentDTOs);
      const equipment = getAutoAddEquipment(parsed);
      if (!equipment) return;

      const dto = equipmentDTOs.find((e) => e.id === equipment.id);
      if (dto) {
        await selectAddOnAsync(planId, {
          id: dto.id ?? equipment.id,
          name: dto.name ?? 'Equipment',
          description: '',
          code: dto.id ?? equipment.id,
          price: 0,
          currency: 'CAD',
          isMandatory: false,
        });
      }
    },
    [sessionId, homeId, queryClient, selectAddOnAsync]
  );

  return { resolve, autoAddEquipment };
};
